import { Badge, Button, Checkbox, Group, Stack } from '@mantine/core'
import { useStatsigClient } from '@statsig/react-bindings'
import React from 'react'
import {
  useRefinementList,
  type UseRefinementListProps,
} from 'react-instantsearch'
import { STATSIG_EVENTS } from 'providers/statsig'
import { COLOR_NAMES } from 'Theme'
import { translateRefinementLabel } from '../helpers'
import { FilterCollapsibleItem } from './FilterCollapsibleItem'

type FilterCollapsibleListProps = UseRefinementListProps & {
  title: string
  defaultOpened?: boolean
  isNumericResults?: boolean
  suffix?: string
  excludedLabels?: string[]
}

export const FilterCollapsibleList = ({
  title,
  defaultOpened,
  isNumericResults,
  suffix,
  limit = 20,
  showMore = true,
  showMoreLimit = 200,
  excludedLabels = [],
  ...rest
}: FilterCollapsibleListProps) => {
  const { client: statsigClient } = useStatsigClient()
  const { items, refine, toggleShowMore, canToggleShowMore, isShowingMore } =
    useRefinementList({
      ...rest,
      limit,
      showMore,
      showMoreLimit,
    })

  if (!items || items.length <= 1) {
    return null
  }

  if (isNumericResults) {
    items.sort((a, b) => Number(a.label) - Number(b.label))
  } else {
    items.sort((a, b) => a.label.localeCompare(b.label))
  }
  return (
    <FilterCollapsibleItem
      title={title}
      defaultOpened={defaultOpened}
      onClick={() => {
        if (statsigClient) {
          if (rest.attribute === 'locationTypes') {
            statsigClient.logEvent(STATSIG_EVENTS.FILTER_LOCATION_TYPES)
          }
        }
      }}
    >
      <Stack gap="xs" onClick={(e) => e.stopPropagation()}>
        {items
          .filter(({ label }) =>
            excludedLabels.every(
              (excludedLabel) => label.indexOf(excludedLabel) === -1
            )
          )
          .map((item) => (
            <Checkbox
              key={item.label}
              label={
                <Group justify="space-between" w="100%" wrap="nowrap">
                  {translateRefinementLabel({
                    value: item.label,
                    suffix,
                    attribute: rest.attribute,
                  })}{' '}
                  <Badge
                    variant="light"
                    color="var(--mantine-color-piney-lake-1)"
                    fw={500}
                    style={{
                      whiteSpace: 'nowrap',
                      borderRadius: '20px',
                      paddingLeft: '0.25rem',
                      paddingRight: '0.25rem',
                      minWidth: '20px',
                    }}
                  >
                    {item.count}
                  </Badge>
                </Group>
              }
              checked={item.isRefined}
              onChange={() => refine(item.value)}
              styles={{
                label: { cursor: 'pointer' },
                input: { cursor: 'pointer' },
                labelWrapper: { width: '100%' },
              }}
            />
          ))}
      </Stack>
      {canToggleShowMore && (
        <Button
          onClick={toggleShowMore}
          variant="transparent"
          ta="left"
          fs="14px"
          lts="0.1em"
          mt="22px"
          color={COLOR_NAMES.EEIRE_BLACK}
          td="underline"
          fw={400}
          tt="uppercase"
          px={0}
        >
          {isShowingMore ? 'Show less' : 'Show more'}
        </Button>
      )}
    </FilterCollapsibleItem>
  )
}
